export const updateSEOTags = (params: {
  title?: string;
  description?: string;
  keywords?: string[];
  locationName?: string;
}) => {
  const { title, description, keywords = [], locationName } = params;

  // Update page title
  if (title) {
    document.title = title;
  }

  // Helper function to update or create meta tag
  const updateMetaTag = (name: string, content: string, property?: string) => {
    const selector = property ? `meta[property="${property}"]` : `meta[name="${name}"]`;
    let metaTag = document.querySelector(selector) as HTMLMetaElement;
    
    if (!metaTag) {
      metaTag = document.createElement('meta');
      if (property) {
        metaTag.setAttribute('property', property);
      } else {
        metaTag.setAttribute('name', name);
      }
      document.head.appendChild(metaTag);
    }
    
    metaTag.setAttribute('content', content);
  };

  // Update meta description
  if (description) {
    updateMetaTag('description', description);
    updateMetaTag('og:description', description, 'og:description');
    updateMetaTag('twitter:description', description, 'twitter:description');
  }

  // Update meta keywords
  if (keywords.length > 0) {
    const keywordString = keywords.join(', ');
    updateMetaTag('keywords', keywordString);
  }

  // Update Open Graph title
  if (title) {
    updateMetaTag('og:title', title, 'og:title');
    updateMetaTag('twitter:title', title, 'twitter:title');
  }

  // Update structured data (JSON-LD)
  if (locationName && keywords.length > 0) {
    updateStructuredData(locationName, keywords, description);
  }
};

const updateStructuredData = (locationName: string, keywords: string[], description?: string) => {
  // Remove existing structured data
  const existingScript = document.querySelector('script[type="application/ld+json"]#dynamic-seo');
  if (existingScript) {
    existingScript.remove();
  }

  // Create new structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `FindFunNow - ${locationName}`,
    "description": description || `Discover activities, events, and accommodations in ${locationName}`,
    "keywords": keywords,
    "about": {
      "@type": "Place",
      "name": locationName
    },
    "mainEntity": {
      "@type": "TravelAgency",
      "name": "FindFunNow",
      "description": "Search activities, events, and hotels worldwide"
    }
  };

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = 'dynamic-seo';
  script.textContent = JSON.stringify(structuredData);
  document.head.appendChild(script);
};

export const generateSEOContent = (locationName: string, eventCount: number, keywords: string[]) => {
  const title = `${locationName} Activities & Events - FindFunNow | ${eventCount} Things to Do`;
  
  const description = keywords.length > 0 
    ? `Discover ${eventCount} amazing activities in ${locationName}. Find ${keywords.slice(0, 3).join(', ')} and more. Book hotels, events, and experiences.`
    : `Discover ${eventCount} amazing activities, events, and accommodations in ${locationName}. Book your perfect experience with FindFunNow.`;

  return { title, description };
};